import {useRef, useState} from 'react';
import {graphql} from 'react-relay';
import {fetchQuery} from 'relay-runtime';
import environment from '../../../RelayEnvironment';

const useInvoiceClientContactsQuery = graphql`
	query useInvoiceClientContactsQuery($clientId: String!, $first: Int!) {
		viewer {
			company {
				client(id: $clientId) {
					primaryContact {
						id
						firstName
						lastName
					}
					clientContacts(first: $first) {
						edges {
							node {
								id
								firstName
								lastName
							}
						}
					}
				}
			}
		}
	}
`;

const MAX_CONTACTS = 1000;

export interface InvoiceContact {
	id: string;
	name: string;
}

interface ClientContactNode {
	id: string;
	firstName: string | null;
	lastName: string | null;
}

interface UseInvoiceClientContactsQueryResponse {
	viewer?: {
		company?: {
			client?: {
				primaryContact?: ClientContactNode | null;
				clientContacts?: {
					edges: Array<{node: ClientContactNode}>;
				};
			} | null;
		};
	};
}

const toContact = (node: ClientContactNode): InvoiceContact => ({
	id: node.id,
	name: [node.firstName, node.lastName].filter(Boolean).join(' '),
});

export interface ClientContactsState {
	contacts: InvoiceContact[];
	loading: boolean;
}

export interface UseInvoiceClientContacts {
	ensureLoaded: (clientId: string | null | undefined) => void;
	getContacts: (clientId: string | null | undefined) => ClientContactsState;
	// `rowKey` scopes the explicit selection (e.g. a project id for a per-row picker, or the
	// client id itself for a single client-wide picker) - two different rowKeys never share a
	// selection even when they resolve to the same `clientId`. `clientId` is only consulted as a
	// one-time fallback: if `rowKey` has no explicit selection yet, the row's own project contact
	// (if `projectContactId` is given and still one of the client's active contacts) wins first,
	// then the client's active primary contact (once loaded).
	getSelectedContact: (
		rowKey: string | null | undefined,
		clientId?: string | null,
		projectContactId?: string | null
	) => InvoiceContact | null;
	setSelectedContact: (rowKey: string, contact: InvoiceContact | null) => void;
}

// Fetches and caches each client's active contacts exactly once for the modal's lifetime (spec:
// "Per-client data loading"). Selection is owned per `rowKey` - the caller decides what a "row" is
// (a project, in the per-row billable table; the single client, in the manual/deposit field) - so
// two rows sharing the same client never affect each other's explicit choice. Call `ensureLoaded`
// from a useEffect for every client id that becomes relevant; repeat calls for an already-loaded/
// loading client are a no-op, so a large project table with many rows of the same client never
// fans out one query per row.
export const useInvoiceClientContacts = (): UseInvoiceClientContacts => {
	const [contactsByClientId, setContactsByClientId] = useState<Record<string, InvoiceContact[]>>({});
	const [loadingClientIds, setLoadingClientIds] = useState<Set<string>>(new Set());
	const [primaryContactByClientId, setPrimaryContactByClientId] = useState<Record<string, InvoiceContact | null>>({});
	const [selectionByRowKey, setSelectionByRowKey] = useState<Record<string, InvoiceContact | null>>({});
	const requestedClientIds = useRef<Set<string>>(new Set());

	const ensureLoaded: UseInvoiceClientContacts['ensureLoaded'] = clientId => {
		if (!clientId || requestedClientIds.current.has(clientId)) return;
		requestedClientIds.current.add(clientId);
		setLoadingClientIds(prev => new Set(prev).add(clientId));

		fetchQuery(environment.getInstance(), useInvoiceClientContactsQuery, {clientId, first: MAX_CONTACTS})
			.toPromise()
			.then(
				(data: UseInvoiceClientContactsQueryResponse | null | undefined) => {
					const client = data?.viewer?.company?.client;
					const nodes = client?.clientContacts?.edges.map(e => e.node) ?? [];
					const contacts = nodes.map(toContact);

					setContactsByClientId(prev => ({...prev, [clientId]: contacts}));
					setLoadingClientIds(prev => {
						const next = new Set(prev);
						next.delete(clientId);
						return next;
					});

					// Cache the active primary contact as this client's one-time default - `primaryContact`
					// resolves regardless of status while `clientContacts` is already ACTIVE-filtered, so
					// membership in the resolved `contacts` list is the "still active" guard. Each row that
					// hasn't made its own explicit choice yet falls back to this (see getSelectedContact).
					const activePrimary =
						client?.primaryContact && contacts.some(c => c.id === client.primaryContact?.id)
							? toContact(client.primaryContact)
							: null;
					setPrimaryContactByClientId(prev => ({...prev, [clientId]: activePrimary}));
				},
				() => {
					// Fetch failed (network error, transient 5xx, etc). Clear the loading flag so
					// `getContacts` falls back to its existing empty-state UI instead of spinning
					// forever, and remove `clientId` from `requestedClientIds` so a later `ensureLoaded`
					// call for the same client (e.g. a transient blip that's since cleared) gets a fresh
					// attempt instead of being no-op'd for the rest of the modal's lifetime. This only
					// affects the failure path - the "fetch each client's contacts at most once
					// concurrently" contract for a successful or still-in-flight load is unchanged (that
					// guard is `requestedClientIds.has(clientId)` at the top of `ensureLoaded`, checked
					// before this failure branch could ever run).
					requestedClientIds.current.delete(clientId);
					setLoadingClientIds(prev => {
						const next = new Set(prev);
						next.delete(clientId);
						return next;
					});
				}
			);
	};

	const getContacts: UseInvoiceClientContacts['getContacts'] = clientId => {
		if (!clientId) return {contacts: [], loading: false};
		return {contacts: contactsByClientId[clientId] ?? [], loading: loadingClientIds.has(clientId)};
	};

	const getSelectedContact: UseInvoiceClientContacts['getSelectedContact'] = (rowKey, clientId, projectContactId) => {
		if (!rowKey) return null;
		// An explicit choice (including an explicit clear-to-null) always wins over either default -
		// `in` distinguishes "never touched" (falls through to the defaults below) from "explicitly
		// cleared" (stays null), since both would otherwise look like `?? null`.
		if (rowKey in selectionByRowKey) return selectionByRowKey[rowKey];

		// The row's own project contact is the next default, but only while it's still one of the
		// client's active contacts - the same "still active" guard already applied to the primary
		// contact below, reusing the same loaded list rather than trusting a possibly-stale id.
		if (clientId && projectContactId) {
			const activeContacts = contactsByClientId[clientId] ?? [];
			const projectContact = activeContacts.find(contact => contact.id === projectContactId);
			if (projectContact) return projectContact;
		}

		return clientId ? primaryContactByClientId[clientId] ?? null : null;
	};

	const setSelectedContact: UseInvoiceClientContacts['setSelectedContact'] = (rowKey, contact) => {
		setSelectionByRowKey(prev => ({...prev, [rowKey]: contact}));
	};

	return {ensureLoaded, getContacts, getSelectedContact, setSelectedContact};
};
